import { supabase, invokeFunction } from '@/lib/supabase';
import {
  getActiveHouseholdPin,
  setActiveHouseholdPin,
  clearStaleActiveHousehold,
} from '@/lib/active-household-pin';
import { parseInvokeErrorBody } from '@/lib/invoke-error';
import { isSchemaNotReadyError } from '@/lib/deployWindow';
import { AlreadyInHouseholdError } from './invitation.service';
import type {
  Household,
  HouseholdUpdate,
  CreateHouseholdInput,
  CaregiverRole,
  HouseholdMemberWithUser,
  InboxAddress,
  Subscription,
} from '@/types';
import type { Child } from '@/types';

export interface OldestMembership {
  householdId: string;
  role: CaregiverRole;
  /**
   * The member's family role ("Mom") in THIS household — per membership, not
   * per account (YAR-485). Optional so callers that only need the access
   * level can build one without it.
   */
  familyRole?: string | null;
}

// The pin itself lives in @/lib/active-household-pin (dependency-free so the
// household store can seed from it without importing this service). Re-
// exported here so existing `householdService.getActiveHouseholdPin(...)`
// callers keep working unchanged.
export {
  getActiveHouseholdPin,
  setActiveHouseholdPin,
  clearStaleActiveHousehold,
} from '@/lib/active-household-pin';

/**
 * Resolve a user's DEFAULT household_members row by the OLDEST created_at.
 * This is the fallback when there is no usable pin — it is what a user lands
 * on at first sign-in, and what the digest job's edge-side
 * resolveMembershipByUser mirrors so both runtimes agree.
 *
 * A user may belong to SEVERAL households (only ownership is singular), so
 * "oldest" is a default, not the whole story — `resolveActiveMembership`
 * below prefers the household the user actually last had open.
 */
export async function getOldestMembershipForUser(
  dbUserId: string,
): Promise<OldestMembership | null> {
  let { data, error } = await supabase
    .from('household_members')
    .select('household_id, role, family_role')
    .eq('user_id', dbUserId)
    .eq('status', 'active')
    .order('created_at', { ascending: true })
    // Deterministic tie-break: identical created_at timestamps would
    // otherwise leave "oldest" ambiguous, so fall back to the row id.
    .order('id', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error && isSchemaNotReadyError(error)) {
    // Deploy-window guard (lib/deployWindow.ts): this is the BOOT path, so a
    // missing family_role column must not stop the app from resolving its
    // household. Same query without it; the role reads as unset.
    const legacy = await supabase
      .from('household_members')
      .select('household_id, role')
      .eq('user_id', dbUserId)
      .eq('status', 'active')
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle();
    data = legacy.data ? { ...legacy.data, family_role: null } : null;
    error = legacy.error;
  }

  if (error) throw error;
  if (!data) return null;
  return {
    householdId: data.household_id,
    role: data.role as CaregiverRole,
    familyRole: data.family_role,
  };
}

/**
 * The household the app should show: the pinned one when the user still has
 * an ACTIVE membership in it, else the oldest (and the stale pin is dropped).
 *
 * The role is read from the SAME row, because a user's access level is
 * per-household — owner of theirs, viewer in another — so switching
 * households must always re-resolve the role, never carry the old one over.
 */
export async function resolveActiveMembership(
  dbUserId: string,
): Promise<OldestMembership | null> {
  const pinned = getActiveHouseholdPin();
  if (pinned) {
    let { data, error } = await supabase
      .from('household_members')
      .select('household_id, role, family_role')
      .eq('user_id', dbUserId)
      .eq('household_id', pinned)
      .eq('status', 'active')
      .maybeSingle();
    if (error && isSchemaNotReadyError(error)) {
      // Deploy-window guard — see getOldestMembershipForUser.
      const legacy = await supabase
        .from('household_members')
        .select('household_id, role')
        .eq('user_id', dbUserId)
        .eq('household_id', pinned)
        .eq('status', 'active')
        .maybeSingle();
      data = legacy.data ? { ...legacy.data, family_role: null } : null;
      error = legacy.error;
    }
    if (data) {
      return {
        householdId: data.household_id,
        role: data.role as CaregiverRole,
        familyRole: data.family_role,
      };
    }
    // Pin points at a household the user no longer belongs to.
    clearStaleActiveHousehold();
  }
  const fallback = await getOldestMembershipForUser(dbUserId);
  if (fallback) setActiveHouseholdPin(fallback.householdId);
  return fallback;
}

/**
 * resolveActiveMembership for the currently authenticated caller.
 *
 * Prefer passing a known dbUserId when you already have the profile — this
 * variant costs the auth.getUser() + users lookup that the caller may have
 * just done. It exists for surfaces (the shell, the switcher) that have a
 * session but no profile in hand.
 */
export async function resolveActiveMembershipForCurrentUser(): Promise<OldestMembership | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: dbUser, error } = await supabase
    .from('users')
    .select('id')
    .eq('supabase_id', user.id)
    .maybeSingle();

  if (error) throw error;
  if (!dbUser) return null;

  return await resolveActiveMembership(dbUser.id);
}

export interface MyHousehold {
  id: string;
  name: string;
  emoji: string | null;
  emojiColor: string | null;
  imagePath: string | null;
  role: CaregiverRole;
}

/**
 * Every household the user actively belongs to, with their per-household
 * access level — the switcher's data source. Ordered oldest-first so the
 * list is stable and matches the default resolution.
 */
export async function getMyHouseholds(
  dbUserId: string,
): Promise<MyHousehold[]> {
  const { data, error } = await supabase
    .from('household_members')
    .select(
      'role, households(id, name, emoji_avatar, emoji_color, avatar_image_path)',
    )
    .eq('user_id', dbUserId)
    .eq('status', 'active')
    .order('created_at', { ascending: true });

  if (error) throw error;

  type Row = {
    role: string;
    households: {
      id: string;
      name: string | null;
      emoji_avatar: string | null;
      emoji_color: string | null;
      avatar_image_path: string | null;
    } | null;
  };

  return ((data ?? []) as unknown as Row[])
    .filter((r) => r.households != null)
    .map((r) => ({
      id: r.households!.id,
      name: r.households!.name ?? '',
      emoji: r.households!.emoji_avatar,
      emojiColor: r.households!.emoji_color,
      imagePath: r.households!.avatar_image_path,
      role: r.role as CaregiverRole,
    }));
}

/**
 * getMyHouseholds for the *currently authenticated* caller, resolving the
 * users row itself. Surfaces that only need "does this user belong to more
 * than one household?" (to decide whether a Switch entry is meaningful)
 * shouldn't have to thread a dbUserId down to reach the answer.
 *
 * Returns [] rather than throwing when there is no session or no users row:
 * callers use this to decide whether to render an affordance, and a signed-
 * out/unprovisioned user simply has nothing to switch between.
 */
export async function getMyHouseholdsForCurrentUser(): Promise<MyHousehold[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  const { data: dbUser, error } = await supabase
    .from('users')
    .select('id')
    .eq('supabase_id', user.id)
    .maybeSingle();

  if (error) throw error;
  if (!dbUser) return [];

  return await getMyHouseholds(dbUser.id);
}

export async function getHousehold(
  householdId: string,
): Promise<Household | null> {
  const { data, error } = await supabase
    .from('households')
    .select()
    .eq('id', householdId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function updateHousehold(
  householdId: string,
  updates: HouseholdUpdate,
): Promise<Household> {
  const { data, error } = await supabase
    .from('households')
    .update(updates)
    .eq('id', householdId)
    .select()
    .single();

  if (error) throw error;
  return data!;
}

export async function getMembers(
  householdId: string,
): Promise<HouseholdMemberWithUser[]> {
  // The household_members SELECT RLS policy only gates on whether the
  // CALLER's own membership is active for this household_id — it does
  // not filter individual rows by their own status. Since removal/leave
  // is now a soft-delete (status='removed'/'left' rows persist), scope
  // explicitly here so a removed/left member doesn't keep showing up in
  // the Settings members list.
  const { data, error } = await supabase
    .from('household_members')
    .select(
      '*, users(id, display_name, email, avatar_emoji, avatar_color, avatar_image_path)',
    )
    .eq('household_id', householdId)
    .eq('status', 'active')
    .returns<HouseholdMemberWithUser[]>();

  if (error) throw error;
  return data ?? [];
}

export async function getChildren(householdId: string): Promise<Child[]> {
  const { data, error } = await supabase
    .from('children')
    .select()
    .eq('household_id', householdId);

  if (error) throw error;
  return data ?? [];
}

export async function getInboxAddress(
  householdId: string,
): Promise<InboxAddress | null> {
  const { data, error } = await supabase
    .from('inbox_addresses')
    .select()
    .eq('household_id', householdId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function getSubscription(
  householdId: string,
): Promise<Subscription | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select()
    .eq('household_id', householdId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Returns the count of non-rejected emails received for the household
 * during the current UTC month. Backed by `v_email_usage_monthly`.
 * Returns 0 if there is no row for the current month.
 */
export async function getMonthlyEmailUsage(
  householdId: string,
): Promise<number> {
  const periodStart = new Date(
    Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1),
  ).toISOString();

  const { data, error } = await supabase
    .from('v_email_usage_monthly')
    .select('email_count')
    .eq('household_id', householdId)
    .eq('period_start', periodStart)
    .maybeSingle();

  if (error) throw error;
  return Number(data?.email_count ?? 0);
}

/** The household's current quota window, as the server computes it. */
export interface EmailQuotaPeriod {
  /**
   * Emails APPROVED inside the window. Dismissed emails do NOT consume
   * quota — only emails that committed tasks/events count.
   */
  used: number;
  /**
   * ISO instant at which the window resets: one month from the
   * household's `tier_started_at`, clamped for short months. Null only
   * if the household has no row (it always should).
   */
  periodEnd: string | null;
}

/**
 * Returns the household's approved-email usage AND the boundary that
 * usage resets on, in one request. Backed by
 * `v_email_approved_current_period`, which is the same source the
 * approval gate reads — so the "Resets …" label can never drift away
 * from the limit it describes.
 *
 * The window follows the SUBSCRIPTION anniversary (one month from
 * `tier_started_at`), not the UTC calendar month: George Tellechea,
 * 2026-08-19, "the date my free account refreshes does not match with
 * the date my subscription will renew". See migration 20260819120000.
 */
export async function getEmailQuotaPeriod(
  householdId: string,
): Promise<EmailQuotaPeriod> {
  const { data, error } = await supabase
    .from('v_email_approved_current_period')
    .select('approved_count, period_end')
    .eq('household_id', householdId)
    .maybeSingle();

  if (error) throw error;
  return {
    used: Number(data?.approved_count ?? 0),
    periodEnd: data?.period_end ?? null,
  };
}

/**
 * Approved-email count only, for callers that don't render the reset
 * boundary (the sidebar emails-processed card). Name kept for caller
 * compatibility across two semantic moves: reviewed → approved-only
 * (migration 20260515000001) and calendar month → subscription
 * anniversary (migration 20260819120000).
 */
export async function getMonthlyEmailsReviewed(
  householdId: string,
): Promise<number> {
  const { used } = await getEmailQuotaPeriod(householdId);
  return used;
}

/**
 * Returns the household's LIFETIME count of approved emails — every email
 * with `processed_disposition = 'APPROVED'`, across all time (no month or
 * tier window, unlike `getMonthlyEmailsReviewed`). Backs the one-time
 * post-5-approvals feedback prompt. Uses a head-only COUNT so no rows are
 * transferred.
 */
export async function getLifetimeEmailsApproved(
  householdId: string,
): Promise<number> {
  const { count, error } = await supabase
    .from('emails')
    .select('id', { count: 'exact', head: true })
    .eq('household_id', householdId)
    .eq('processed_disposition', 'APPROVED');

  if (error) throw error;
  return count ?? 0;
}

/**
 * Creates a new household for the current user (as PRIMARY).
 *
 * YAR-269: households-create now 409s with `{code: 'ALREADY_IN_HOUSEHOLD'}`
 * when the caller already belongs to a household — Yaruta supports at most
 * one household per account. FunctionsHttpError hides the body behind
 * `error.context`, so parse it the same way `acceptInvitation` does and
 * surface the same typed, user-facing error rather than a raw supabase-js
 * error.
 */
export async function createHousehold(
  input: CreateHouseholdInput,
): Promise<Household> {
  const { data, error } = await invokeFunction('households-create', {
    body: input,
  });

  if (error) {
    const body = await parseInvokeErrorBody(error);
    if (body && body.code === 'ALREADY_IN_HOUSEHOLD') {
      throw new AlreadyInHouseholdError();
    }
    throw error;
  }
  return data as Household;
}

export type EnsureHouseholdResult =
  | { outcome: 'created'; householdId: string }
  | { outcome: 'resumed' };

/**
 * Create the caller's household, or find that they already own one.
 *
 * The fork's admin choice and the wait-state's "Set up my family instead"
 * both land here (YAR-438). Until then the household was created by
 * `users-provision` at first sign-in — which meant a helper who signed in
 * before their invite existed owned an empty household for ever. Now nothing
 * creates one until the parent says they are the one who gets the school
 * emails, and saying it twice is safe: `households-create` answers
 * `409 ALREADY_IN_HOUSEHOLD` for an owner, and that is the resume the story
 * asks for ("guard the write, not the route"), not a failure.
 *
 * Anything else is rethrown so the screen can say so and keep the choice
 * uncommitted.
 */
export async function ensureHousehold(
  name: string,
): Promise<EnsureHouseholdResult> {
  try {
    const household = await createHousehold({ name });
    return { outcome: 'created', householdId: household.id };
  } catch (err) {
    if (err instanceof AlreadyInHouseholdError) return { outcome: 'resumed' };
    throw err;
  }
}

export async function transferOwnership(
  householdId: string,
  newOwnerUserId: string,
): Promise<void> {
  const { error } = await invokeFunction('household-transfer', {
    body: { household_id: householdId, new_owner_user_id: newOwnerUserId },
  });
  if (error) throw error;
}

export async function removeMember(memberId: string): Promise<void> {
  // Soft-delete: the row is retained (status='removed') rather than
  // hard-deleted so history/audit stays intact and a re-invite of the
  // same user can reactivate the row via upsert instead of hitting the
  // (user_id, household_id) unique constraint. RLS helpers already
  // filter to status='active', so a removed row is invisible to normal
  // reads without any policy change.
  const { error } = await supabase
    .from('household_members')
    .update({ status: 'removed' })
    .eq('id', memberId);
  if (error) throw error;
}

/**
 * Change an existing member's access level. `role` is intentionally typed
 * to exclude PRIMARY — ownership moves only via transfer, never a direct
 * role edit. The DB is the real gate: the household_members write policies
 * (manager_write with `role <> 'PRIMARY'`, owner_write for PRIMARY) reject
 * demoting/impersonating the owner or minting a second owner, so a
 * disallowed edit affects 0 rows / errors regardless of this signature.
 */
export async function updateMemberRole(
  memberId: string,
  role: Exclude<CaregiverRole, 'PRIMARY'>,
): Promise<void> {
  const { error } = await supabase
    .from('household_members')
    .update({ role })
    .eq('id', memberId);
  if (error) throw error;
}

export async function leaveHousehold(householdId: string): Promise<void> {
  // Soft-delete via the leave_household RPC (SECURITY DEFINER). A direct
  // UPDATE can't work here: the per-command household_members policies
  // (owner_write/manager_write) intentionally don't let an Editor/Viewer
  // write their own row, so the old client-side update silently matched
  // 0 rows for those roles. The RPC pins the whole write server-side —
  // own active non-PRIMARY row only, status='left' only — and refuses the
  // owner ('owner_cannot_leave': ownership exits only via transfer).
  const { error } = await supabase.rpc('leave_household', {
    p_household_id: householdId,
  });
  if (error) throw error;
}

export async function updateGmailSubscriptionStatus(
  householdId: string,
  active: boolean,
): Promise<void> {
  // gmail_subscriptions is service_role only — requires an edge function
  const { error } = await invokeFunction('gmail-subscription-toggle', {
    body: { household_id: householdId, active },
  });
  if (error) throw error;
}

/**
 * Tear down the Gmail integration for a household: stop the watch, revoke
 * the OAuth refresh token at Google, and clear `refresh_token` +
 * `watch_expiry` on our side. The subscription row, inbox address, and
 * approved senders are preserved so the user can reconnect via OAuth and
 * pick up where they left off.
 *
 * Sibling of `updateGmailSubscriptionStatus(false)` — both end with
 * `active=false`, but `disconnectGmailSubscription` cannot be undone with
 * a toggle: the user must re-OAuth to obtain new tokens.
 */
export async function disconnectGmailSubscription(
  householdId: string,
): Promise<void> {
  const { error } = await invokeFunction('gmail-subscription-disconnect', {
    body: { household_id: householdId },
  });
  if (error) throw error;
}

export interface RefreshGmailResult {
  ok: boolean;
  /** True when the stored refresh_token is dead and the caller needs to fall back to a full OAuth flow. */
  needsOAuth: boolean;
}

/**
 * Server-side reconnect using the stored refresh_token. Refreshes the
 * Google access token and rearms the Gmail watch — no client-side OAuth
 * required as long as our refresh_token still works.
 *
 * Useful after the 7-day Gmail watch lapses: if the cron renewal hasn't
 * caught it (or if the user paused for >7 days and now wants back),
 * this puts them back online without forcing them through Google
 * consent again.
 *
 * Returns `{ ok: true }` on success or `{ ok: false, needsOAuth: true }`
 * when the stored token is no longer valid (caller should fall back to
 * `signInWithOAuth`).
 */
export async function refreshGmailSubscription(
  householdId: string,
): Promise<RefreshGmailResult> {
  const { data, error } = await invokeFunction('gmail-subscription-refresh', {
    body: { household_id: householdId },
  });
  if (error) throw error;
  const payload = data as { ok?: boolean; needs_oauth?: boolean };
  return {
    ok: payload?.ok === true,
    needsOAuth: payload?.needs_oauth === true,
  };
}

/**
 * `verify: true` also asks Google whether the stored grant still works, and
 * stamps `revoked_at` when it does not (a parent who removed Yaruta under
 * Google Account → Third-party apps). Costs a token round trip, so only the
 * Email Monitoring screen asks for it — not every status read.
 */
export async function getGmailSubscriptionStatus(
  householdId: string,
  opts?: { verify?: boolean },
): Promise<{
  active: boolean;
  watch_expiry: string | null;
  gmail_address: string | null;
  // Non-null when webhooks-gmail-watch detected an invalid_grant from
  // Google (revoked refresh_token). The store treats this as a hard
  // disconnect — same priority as `active=false` — so the
  // gmail_revoked banner can fire with the right copy. Older deploys
  // of gmail-subscription-status that haven't picked up this field
  // will simply omit it; treating absent as null keeps callers safe.
  revoked_at: string | null;
  // Non-null when a renewal refreshed the token but the follow-up
  // users.watch failed (recoverable — token still valid, unlike
  // revoked_at). The store maps this to the `needs_attention` state.
  // Older deploys omit it; absent → null.
  watch_renewal_failed_at: string | null;
} | null> {
  const { data, error } = await invokeFunction('gmail-subscription-status', {
    body: {
      household_id: householdId,
      ...(opts?.verify ? { verify: true } : {}),
    },
  });
  if (error) throw error;
  return (data as { data: typeof data })?.data ?? null;
}
